import './ManageQuestions.css';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { getAllQuestions, filterQuestionsByType, searchQuestions, deleteQuestion } from '../../services/questionService';
import { fetchAuthors, searchAuthors } from '../../services/authorService';
import { fetchJournals, searchBooks } from '../../services/bookService';
import Pagination from '../Pagination/Pagination';

const QUESTIONS_PER_PAGE = 12;

const QUESTION_TYPE_LABELS = {
    'Multichoice': 'Výběr z možností',
    'MULTICHOICE': 'Výběr z možností',
    'MultipleCorrect': 'Více správných odpovědí',
    'MULTIPLE_CORRECT': 'Více správných odpovědí',
    'MULTIPLECORRECT': 'Více správných odpovědí',
    'MULTIRESPONSE': 'Více správných odpovědí',
    'FillIn': 'Doplňovačka',
    'FILL_IN': 'Doplňovačka',
    'FILLIN': 'Doplňovačka',
    'Linking': 'Propojovačka',
    'LINKING': 'Propojovačka',
    'Timeline': 'Časová osa',
    'TIMELINE': 'Časová osa',
    'FindInText': 'Najít v textu',
    'FIND_IN_TEXT': 'Najít v textu',
    'FINDINTEXT': 'Najít v textu'
};

// Filter options - backendValue is what gets sent to /filter?questionType=
const QUESTION_TYPE_FILTERS = [
    { key: 'MULTICHOICE', label: 'Výběr z možností', backendValue: 'MULTICHOICE' },
    { key: 'MULTIRESPONSE', label: 'Více správných', backendValue: 'MULTIRESPONSE' },
    { key: 'FILLIN', label: 'Doplňovačka', backendValue: 'FILLIN' },
    { key: 'LINKING', label: 'Propojovačka', backendValue: 'LINKING' },
    { key: 'TIMELINE', label: 'Časová osa', backendValue: 'TIMELINE' },
    { key: 'FINDINTEXT', label: 'Najít v textu', backendValue: 'FINDINTEXT' },
];

function ManageQuestions() {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [activeTypeFilters, setActiveTypeFilters] = useState([]);
    const [authorMap, setAuthorMap] = useState({});
    const [bookMap, setBookMap] = useState({});

    // Entity search (author/book) state
    const [entitySearchTerm, setEntitySearchTerm] = useState('');
    const [debouncedEntitySearch, setDebouncedEntitySearch] = useState('');
    const [entitySearchResults, setEntitySearchResults] = useState({ books: [], authors: [] });
    const [entitySearching, setEntitySearching] = useState(false);
    const [selectedEntityFilters, setSelectedEntityFilters] = useState([]);

    // Load author/book lookup maps for display
    useEffect(() => {
        const loadLookups = async () => {
            try {
                const [authorsData, booksData] = await Promise.all([
                    fetchAuthors(0, 999),
                    fetchJournals(0, 999),
                ]);
                const aMap = {};
                (authorsData.content || []).forEach(a => { aMap[a.id] = a.name; });
                setAuthorMap(aMap);

                const bMap = {};
                (booksData.content || []).forEach(b => { bMap[b.id] = b.bookTitle; });
                setBookMap(bMap);
            } catch (err) {
                console.error('Error loading lookups:', err);
            }
        };
        loadLookups();
    }, []);

    // Debounce entity search
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedEntitySearch(entitySearchTerm);
        }, 500);
        return () => clearTimeout(timer);
    }, [entitySearchTerm]);

    // Search both books and authors + match "Literární teorie"
    useEffect(() => {
        const performSearch = async () => {
            if (debouncedEntitySearch.trim().length < 2) {
                setEntitySearchResults({ books: [], authors: [], showUnlinked: false });
                return;
            }
            try {
                setEntitySearching(true);
                const term = debouncedEntitySearch.trim();
                const bookFilters = { title: term, authorName: term };
                const [bookResults, authorResults] = await Promise.all([
                    searchBooks(bookFilters, 0, 5),
                    searchAuthors(term, 0, 5)
                ]);
                const showUnlinked = 'literární teorie'.includes(term.toLowerCase());
                setEntitySearchResults({
                    books: bookResults.content || [],
                    authors: authorResults.content || [],
                    showUnlinked
                });
            } catch (err) {
                console.error('Error searching:', err);
                setEntitySearchResults({ books: [], authors: [], showUnlinked: false });
            } finally {
                setEntitySearching(false);
            }
        };
        performSearch();
    }, [debouncedEntitySearch]);

    const handleSelectBook = (book) => {
        const alreadySelected = selectedEntityFilters.some(
            item => item.type === 'book' && item.data.id === book.id
        );
        if (alreadySelected) return;
        setSelectedEntityFilters(prev => [...prev, { type: 'book', data: book }]);
        setEntitySearchTerm('');
        setEntitySearchResults({ books: [], authors: [] });
        setCurrentPage(0);
    };

    const handleSelectAuthor = (author) => {
        const alreadySelected = selectedEntityFilters.some(
            item => item.type === 'author' && item.data.id === author.id
        );
        if (alreadySelected) return;
        setSelectedEntityFilters(prev => [...prev, { type: 'author', data: author }]);
        setEntitySearchTerm('');
        setEntitySearchResults({ books: [], authors: [] });
        setCurrentPage(0);
    };

    const handleSelectUnlinked = () => {
        const alreadySelected = selectedEntityFilters.some(item => item.type === 'unlinked');
        if (alreadySelected) return;
        setSelectedEntityFilters(prev => [...prev, { type: 'unlinked' }]);
        setEntitySearchTerm('');
        setEntitySearchResults({ books: [], authors: [], showUnlinked: false });
        setCurrentPage(0);
    };

    const handleRemoveEntityFilter = (index) => {
        setSelectedEntityFilters(prev => prev.filter((_, i) => i !== index));
        setCurrentPage(0);
    };

    const loadQuestions = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            let allContent = [];
            let combinedTotalElements = 0;

            const selectedAuthor = selectedEntityFilters.find(f => f.type === 'author');
            const selectedBook = selectedEntityFilters.find(f => f.type === 'book');
            const selectedUnlinked = selectedEntityFilters.some(f => f.type === 'unlinked');
            const hasEntityFilter = selectedAuthor || selectedBook || selectedUnlinked;

            if (hasEntityFilter) {
                // Use search endpoint when author/book/unlinked filters are active
                const pageData = await searchQuestions({
                    authorId: selectedAuthor?.data.id,
                    readingJournalId: selectedBook?.data.id,
                    unlinked: selectedUnlinked || undefined,
                    page: currentPage,
                    size: QUESTIONS_PER_PAGE
                });
                if (pageData.content) {
                    allContent = pageData.content;
                    combinedTotalElements = pageData.page?.totalElements || pageData.content.length;
                } else if (Array.isArray(pageData)) {
                    allContent = pageData;
                    combinedTotalElements = pageData.length;
                }

                // Client-side type filtering on search results
                if (activeTypeFilters.length > 0) {
                    allContent = allContent.filter(q => activeTypeFilters.includes(q.questionType));
                    combinedTotalElements = allContent.length;
                }
            } else if (activeTypeFilters.length === 0) {
                // No filter - use regular endpoint
                const pageData = await getAllQuestions(currentPage, QUESTIONS_PER_PAGE);
                if (pageData.content) {
                    allContent = pageData.content;
                    combinedTotalElements = pageData.page?.totalElements || pageData.content.length;
                } else if (Array.isArray(pageData)) {
                    allContent = pageData;
                    combinedTotalElements = pageData.length;
                }
            } else if (activeTypeFilters.length === 1) {
                // Single filter - use filter endpoint with server paging
                const filter = QUESTION_TYPE_FILTERS.find(f => f.key === activeTypeFilters[0]);
                const pageData = await filterQuestionsByType(filter.backendValue, currentPage, QUESTIONS_PER_PAGE);
                if (pageData.content) {
                    allContent = pageData.content;
                    combinedTotalElements = pageData.page?.totalElements || pageData.content.length;
                } else if (Array.isArray(pageData)) {
                    allContent = pageData;
                    combinedTotalElements = pageData.length;
                }
            } else {
                // Multiple filters - parallel calls, merge results
                const selectedFilters = activeTypeFilters.map(key =>
                    QUESTION_TYPE_FILTERS.find(f => f.key === key)
                );

                // First get total counts for each type (page 0, size 0 won't work, so get page 0 with size 1 just for counts)
                const countPromises = selectedFilters.map(f =>
                    filterQuestionsByType(f.backendValue, 0, 1)
                );
                const countResults = await Promise.all(countPromises);
                combinedTotalElements = countResults.reduce((sum, r) => {
                    return sum + (r.page?.totalElements || r.content?.length || 0);
                }, 0);

                // Now figure out which types to fetch for the current page
                // Build an ordered list of (type, count) and calculate which items fall on the current page
                const typeCounts = selectedFilters.map((f, i) => ({
                    filter: f,
                    total: countResults[i].page?.totalElements || countResults[i].content?.length || 0
                }));

                const startIndex = currentPage * QUESTIONS_PER_PAGE;
                const endIndex = startIndex + QUESTIONS_PER_PAGE;
                let accumulated = 0;

                const fetchPromises = [];

                for (const tc of typeCounts) {
                    const typeStart = accumulated;
                    const typeEnd = accumulated + tc.total;

                    if (typeEnd > startIndex && typeStart < endIndex) {
                        // This type has items on the current page
                        const skipInType = Math.max(0, startIndex - typeStart);
                        const takeFromType = Math.min(tc.total - skipInType, endIndex - Math.max(startIndex, typeStart));
                        const typePage = Math.floor(skipInType / QUESTIONS_PER_PAGE);
                        const typeSize = takeFromType + (skipInType % QUESTIONS_PER_PAGE);

                        fetchPromises.push(
                            filterQuestionsByType(tc.filter.backendValue, typePage, typeSize)
                                .then(r => {
                                    const content = r.content || [];
                                    const offset = skipInType % QUESTIONS_PER_PAGE;
                                    return content.slice(offset, offset + takeFromType);
                                })
                        );
                    }

                    accumulated += tc.total;
                }

                const results = await Promise.all(fetchPromises);
                allContent = results.flat();
            }

            setQuestions(allContent);
            setTotalElements(combinedTotalElements);
            setTotalPages(Math.max(1, Math.ceil(combinedTotalElements / QUESTIONS_PER_PAGE)));
        } catch (err) {
            console.error('Error loading questions:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [currentPage, activeTypeFilters, selectedEntityFilters]);

    useEffect(() => {
        loadQuestions();
    }, [loadQuestions]);

    const handlePageChange = (newPage) => {
        if (newPage >= 0 && newPage < totalPages) {
            setCurrentPage(newPage);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handleFilterAdd = (filterKey) => {
        if (filterKey && !activeTypeFilters.includes(filterKey)) {
            setActiveTypeFilters(prev => [...prev, filterKey]);
            setCurrentPage(0);
        }
    };

    const handleFilterRemove = (filterKey) => {
        setActiveTypeFilters(prev => prev.filter(k => k !== filterKey));
        setCurrentPage(0);
    };

    const handleBack = () => {
        navigate('/admin');
    };

    const handleEdit = (questionId) => {
        navigate(`/admin/edit-question/${questionId}`);
    };

    const handleDeleteClick = (question) => {
        setDeleteConfirm(question);
    };

    const handleDeleteCancel = () => {
        setDeleteConfirm(null);
    };

    const handleDeleteConfirm = async () => {
        if (!deleteConfirm) return;

        try {
            setDeleting(true);
            await deleteQuestion(deleteConfirm.id);
            setDeleteConfirm(null);
            await loadQuestions();
        } catch (err) {
            setError('Nepodařilo se smazat otázku. Zkuste to prosím znovu.');
            console.error('Error deleting question:', err);
        } finally {
            setDeleting(false);
        }
    };

    // Client-side text search on the loaded page
    const displayedQuestions = searchTerm.trim()
        ? questions.filter(q => q.title?.toLowerCase().includes(searchTerm.toLowerCase()))
        : questions;

    return (
        <div className="admin-page">
            <div className="manage-container">
                <button className="back-button" onClick={handleBack}>
                    ← Zpět na dashboard
                </button>

                <h1 className="page-title">Spravovat otázky</h1>

                <div className="type-filter-section">
                    <select
                        className="type-filter-select"
                        onChange={(e) => {
                            if (e.target.value) {
                                handleFilterAdd(e.target.value);
                                e.target.value = '';
                            }
                        }}
                        value=""
                    >
                        <option value="">Filtrovat dle typu otázky...</option>
                        {QUESTION_TYPE_FILTERS
                            .filter(f => !activeTypeFilters.includes(f.key))
                            .map(filter => (
                                <option key={filter.key} value={filter.key}>
                                    {filter.label}
                                </option>
                            ))}
                    </select>

                    {activeTypeFilters.length > 0 && (
                        <div className="active-filter-tags">
                            {activeTypeFilters.map(key => {
                                const filter = QUESTION_TYPE_FILTERS.find(f => f.key === key);
                                return (
                                    <div key={key} className="filter-tag">
                                        <span className="filter-tag-text">{filter?.label}</span>
                                        <button
                                            type="button"
                                            className="filter-tag-remove"
                                            onClick={() => handleFilterRemove(key)}
                                            title="Zrušit filtr"
                                        >
                                            ✕
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div className="entity-filter-section">
                    {selectedEntityFilters.length > 0 && (
                        <div className="active-filter-tags">
                            {selectedEntityFilters.map((item, index) => (
                                <div key={`${item.type}-${item.data?.id || 'unlinked'}`} className={`filter-tag ${item.type === 'book' ? 'filter-tag-book' : item.type === 'unlinked' ? 'filter-tag-theory' : ''}`}>
                                    <span className="filter-tag-text">
                                        {item.type === 'book' ? item.data.bookTitle : item.type === 'unlinked' ? 'Literární teorie' : item.data.name}
                                    </span>
                                    <button
                                        type="button"
                                        className="filter-tag-remove"
                                        onClick={() => handleRemoveEntityFilter(index)}
                                        title="Zrušit filtr"
                                    >
                                        ✕
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="entity-search-wrapper">
                        <input
                            type="text"
                            className="search-input"
                            value={entitySearchTerm}
                            onChange={(e) => setEntitySearchTerm(e.target.value)}
                            placeholder="Filtrovat dle autora nebo knihy..."
                        />
                        {entitySearching && <p className="entity-search-loading">Hledám...</p>}
                        {(entitySearchResults.books.length > 0 || entitySearchResults.authors.length > 0 || entitySearchResults.showUnlinked) && (
                            <div className="entity-search-results">
                                {entitySearchResults.showUnlinked && (
                                    <div
                                        className="entity-search-result-item"
                                        onClick={handleSelectUnlinked}
                                    >
                                        <span className="result-title">Literární teorie</span>
                                        <span className="result-subtitle">Otázky o literární teorii</span>
                                    </div>
                                )}
                                {entitySearchResults.books.length > 0 && (
                                    <>
                                        <div className="entity-search-section-header">Knihy</div>
                                        {entitySearchResults.books.map(book => (
                                            <div
                                                key={`book-${book.id}`}
                                                className="entity-search-result-item"
                                                onClick={() => handleSelectBook(book)}
                                            >
                                                <span className="result-title">{book.bookTitle}</span>
                                                {book.author?.name && (
                                                    <span className="result-subtitle">{book.author.name}</span>
                                                )}
                                            </div>
                                        ))}
                                    </>
                                )}
                                {entitySearchResults.authors.length > 0 && (
                                    <>
                                        <div className="entity-search-section-header">Autoři</div>
                                        {entitySearchResults.authors.map(author => (
                                            <div
                                                key={`author-${author.id}`}
                                                className="entity-search-result-item"
                                                onClick={() => handleSelectAuthor(author)}
                                            >
                                                <span className="result-title">{author.name}</span>
                                            </div>
                                        ))}
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                <div className="search-bar-container">
                    <input
                        type="text"
                        className="search-input"
                        placeholder="Hledat otázky..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <svg
                        className="search-icon"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                    >
                        <circle cx="11" cy="11" r="8"></circle>
                        <path d="m21 21-4.35-4.35"></path>
                    </svg>
                </div>

                {error && (
                    <div className="alert alert-error">
                        {error}
                    </div>
                )}

                {loading ? (
                    <div className="loading-container">
                        <p>Načítání otázek...</p>
                    </div>
                ) : displayedQuestions.length === 0 ? (
                    <div className="no-questions">
                        <p>{searchTerm.trim() || activeTypeFilters.length > 0 || selectedEntityFilters.length > 0 ? 'Žádné otázky neodpovídají zvoleným filtrům.' : 'Zatím nejsou žádné otázky v databázi.'}</p>
                    </div>
                ) : (
                    <div className="questions-list">
                        {displayedQuestions.map(question => (
                            <div key={question.id} className="question-item">
                                <div className="question-info">
                                    {question.questionType && (
                                        <span className="question-type-badge">
                                            {QUESTION_TYPE_LABELS[question.questionType] || question.questionType}
                                        </span>
                                    )}
                                    <h3 className="question-title">{question.title}</h3>
                                    <div className="question-links">
                                        {question.authorIds?.length > 0 || question.readingJournalIds?.length > 0 ? (
                                            <>
                                                {question.authorIds?.map(id => (
                                                    <span key={id} className="question-link-badge author-link">
                                                        {authorMap[id] || id}
                                                    </span>
                                                ))}
                                                {question.readingJournalIds?.map(id => (
                                                    <span key={id} className="question-link-badge book-link">
                                                        {bookMap[id] || id}
                                                    </span>
                                                ))}
                                            </>
                                        ) : (
                                            <span className="question-link-badge theory-link">
                                                Literární teorie
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="question-actions">
                                    <button
                                        className="btn-action btn-edit"
                                        onClick={() => handleEdit(question.id)}
                                        title="Upravit otázku"
                                    >
                                        ✏️ Upravit
                                    </button>
                                    <button
                                        className="btn-action btn-delete"
                                        onClick={() => handleDeleteClick(question)}
                                        title="Smazat otázku"
                                    >
                                        🗑️ Smazat
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalElements={totalElements}
                    onPageChange={handlePageChange}
                    itemLabel="otázek"
                />

                {/* Delete Confirmation Modal */}
                {deleteConfirm && (
                    <div className="modal-overlay" onClick={handleDeleteCancel}>
                        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                            <h2 className="modal-title">Potvrdit smazání</h2>
                            <p className="modal-text">
                                Opravdu chcete smazat otázku "{deleteConfirm.title}"?
                            </p>
                            <div className="modal-actions">
                                <button
                                    className="modal-button modal-button-cancel"
                                    onClick={handleDeleteCancel}
                                    disabled={deleting}
                                >
                                    Zrušit
                                </button>
                                <button
                                    className="modal-button modal-button-confirm"
                                    onClick={handleDeleteConfirm}
                                    disabled={deleting}
                                >
                                    {deleting ? 'Mazání...' : 'Smazat'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ManageQuestions;
